

/**
 * step 1: 选择对象，若事先选好则跳过
 * step 2: 选择基点，可捕捉
 * step 3: 移动鼠标，根据基点和鼠标连线，计算和局部坐标系x轴夹角，旋转目标clone
 * step 4: 旋转目标
 */

import { EXTRA_SINGAL } from "@/commons/enums/extra-singal";
import { BasicControl, CURSOR_TYPE, dymode, DYMODE_MODE, LineT, Listeners, MOUSE, Point, TnEngineContext, TnEngineExtraContext, ViewEditor } from "pytha";
import { Object3D, Object3DEventMap, Quaternion, Vector3 } from "three";


export class ObjMeshGroupRotateControl extends BasicControl {

    id: number = 10086;

    point_down: Vector3 = new Vector3();
    point_move: Vector3 = new Vector3();
    angle: number;
    angle_last: number;
    step: number = 1;

    capturePoint: Vector3;

    dymodePoint: Vector3 = new Vector3();
    dymodeAngle: number;

    orthogonalPoint: Vector3;

    _helpLine: LineT = new LineT();

    objMeshGroupUuids: string[] = [];
    objMeshGroups: Object3D<Object3DEventMap>[] = [];
    objMeshGroupFirstPoss: Vector3[] = [];
    objMeshGroupFirstQuaternions: Quaternion[] = [];
    firstPoint: Vector3 = new Vector3(0, 0, 0);

    options = {
        2: [
            { name: '放弃', key: 'U' },
        ],
        3: [
            { name: '放弃', key: 'U' },
        ]
    }
    polarPoint: Vector3;
    context: TnEngineContext;
    extraContext: TnEngineExtraContext;
    constructor(editor: ViewEditor, listeners: Listeners, isSubClass: boolean = false) {
        super();
        if (isSubClass) return;
        this.context = TnEngineContext.getInstance();
        this.extraContext = TnEngineExtraContext.getInstance();
        this.editor = editor;
        this.listeners = listeners;
        this._helpLine._isHelpLine = true;

        // this.listeners.signals.emptySelectedEntities.dispatch();
        this.listeners.signals.onPointCapture.add(this.onPointCapture);
        this.listeners.signals.onDymodeChanged.add(this.onDymodeChanged);
        this.listeners.signals.onKeyMouseConfirm.add(this.onKeyMouseConfirm);
        this.listeners.signals.onPointOrthogonal.add(this.onPointOrthogonal);
        this.listeners.signals.onPointPolarTracking.add(this.onPointPolarTracking);
        this.editor.selectControl.setEnabled(true);
        this.extraContext.dymodeContext.enabled = true;
        this.editor.clampControl.enabled = false;
        this.editor.clampControl.hidden = true;
        this.point_down.set(this.extraContext.mousePosContext.x, this.extraContext.mousePosContext.y, this.extraContext.mousePosContext.z);

        this.prevStep();

        console.log('RotateControl added')
    }

    dispose(): void {
        this.listeners.signals.noNeedCapture.dispatch();
        this.listeners.signals.emptySelectedEntities.dispatch();
        this.listeners.getSignal(EXTRA_SINGAL.onObjMeshGroupChange).dispatch();
        this.editor.selectControl.setEnabled(true);
        this.extraContext.dymodeContext.enabled = false;
        this.editor.clampControl.enabled = true;
        this.editor.clampControl.hidden = false;
        this.editor.orthogonalControl.enabled = false;
        this.editor.polarControl.enabled = false;
        this.listeners.signals.onDymodeChanged.remove(this.onDymodeChanged);
        this.editor.renderer.domElement.removeEventListener('pointermove', this.onPointerMove);
        this.editor.renderer.domElement.removeEventListener('pointerdown', this.onPointerDown);
        this.listeners.signals.onPointCapture.remove(this.onPointCapture);
        this.listeners.signals.onKeyMouseConfirm.remove(this.onKeyMouseConfirm);
        this.listeners.signals.onPointOrthogonal.remove(this.onPointOrthogonal);
        this.listeners.signals.onPointPolarTracking.remove(this.onPointPolarTracking);
        if (this._helpLine && this._helpLine.viewObj && this._helpLine.viewObj.parent) {
            this._helpLine.viewObj.parent?.remove(this._helpLine.viewObj);
        }
        console.log('RotateControl removed')
    }

    onPointPolarTracking = (point) => {
        this.polarPoint = point;
    }

    onPointCapture = (point) => {
        this.capturePoint = point;
    }

    onPointOrthogonal = (point) => {
        this.orthogonalPoint = point;
    }

    onPointerDown = (event) => {
        event.stopPropagation();
        if (event.button !== MOUSE.LEFT) return;
        if (this.step === 2) {
            this.point_down.set(this.extraContext.mousePosContext.x, this.extraContext.mousePosContext.y, this.extraContext.mousePosContext.z);
            if (this.orthogonalPoint) {
                this.point_down.copy(this.orthogonalPoint);
            }
            if (this.polarPoint) {
                this.point_down.copy(this.polarPoint);
            }
            if (this.capturePoint) {
                this.point_down.copy(this.capturePoint);
            }
            this.firstStep();
        } else if (this.step === 3) {
            this.secondStep();
        }
    }

    prevStep = () => {
        this.editor.renderer.domElement.addEventListener('pointerdown', this.onPointerDown);
        this.editor.selectControl.setEnabled(false);
        this.listeners.signals.needCapture.dispatch();
        this.step = 2;
        this.extraContext.dymodeContext.setMode(DYMODE_MODE.POINT);
        this.extraContext.dymodeContext.setDymodeTip('选择基点:');
        this.extraContext.stmodeContext.setStmode('选择基点或', this.options[this.step] || []);
        this.listeners.signals.onCursorChange.dispatch(CURSOR_TYPE.CROSS);
    }

    firstStep = () => {
        this._helpLine.set(this.point_down, this.point_down);
        this.firstPoint.copy(this.point_down);
        let view = this._helpLine.render();
        this.extraContext.sceneContext.scene.add(view);

        if (this.objMeshGroupUuids == null || this.objMeshGroupUuids.length === 0) {
            this.listeners.signals.onOpeCommandControlEnd.dispatch();
            return;
        }
        let objMeshGroups = [];
        this.extraContext.sceneContext.scene.children.forEach(obj => {
            if (this.objMeshGroupUuids.includes(obj.uuid)) {
                objMeshGroups.push(obj);
            }
        })
        if (!objMeshGroups || objMeshGroups.length === 0) {
            this.listeners.signals.onOpeCommandControlEnd.dispatch();
            return;
        }
        this.objMeshGroups = objMeshGroups;

        for (let i = 0; i < this.objMeshGroups.length; i++) {
            let objMeshGroup = this.objMeshGroups[i];
            let objMeshGroupFirstQuaternion = objMeshGroup.quaternion.clone();
            this.objMeshGroupFirstQuaternions.push(objMeshGroupFirstQuaternion);

            let objMeshGroupFirstPos = objMeshGroup.position.clone();
            this.objMeshGroupFirstPoss.push(objMeshGroupFirstPos);
        }

        this.step = 3;
        this.angle_last = 0;
        this.editor.renderer.domElement.addEventListener('pointermove', this.onPointerMove);
        this.listeners.signals.orthogonal.dispatch(this.point_down);
        this.listeners.signals.polarTracking.dispatch(this.point_down);
        this.extraContext.dymodeContext.setDymodeTip('指定旋转角度:');
        this.extraContext.dymodeContext.setMode(DYMODE_MODE.ANGLE, this.point_down);
        this.extraContext.stmodeContext.setStmode('指定旋转角度或', this.options[this.step] || []);
        this.listeners.signals.needRender.dispatch();

    }

    secondStep = () => {
        // this.editor.history.execute(new RotateCommand(this.selectedEntities, <Point>this.point_down, this.angle, this.cstEntities));
        if (!this.point_down || !this.angle) {
            this.listeners.signals.onOpeCommandControlEnd.dispatch();
            return
        }

        let axisZ = this.context.ucsContext.getAxisList().axisZ;
        this.objMeshGroups.forEach((objMeshGroup, index) => {
            const objMeshGroupFirstPos = this.objMeshGroupFirstPoss[index];
            const objMeshGroupFirstQuaternion = this.objMeshGroupFirstQuaternions[index];

            //恢复默认位置
            objMeshGroup.position.copy(objMeshGroupFirstPos);
            quaternion.setFromAxisAngle(axisZ, this.angle);
            // 将对象平移到基点坐标系
            objMeshGroup.position.sub(this.firstPoint);
            // 应用旋转
            objMeshGroup.position.applyQuaternion(quaternion);
            // 平移回全局坐标系
            objMeshGroup.position.add(this.firstPoint);
            //恢复默认角度
            objMeshGroup.quaternion.copy(objMeshGroupFirstQuaternion);
            // 如果需要旋转整个对象（包括其方向），应用四元数到对象本身
            objMeshGroup.quaternion.premultiply(quaternion);
        })

        this.step = 4;
        this.editor.listeners.signals.needRender.dispatch();
        this.listeners.signals.onOpeCommandControlEnd.dispatch();
    }

    protected _moveDir = new Vector3();
    onPointerMove = (event) => {
        this.point_move.set(this.extraContext.mousePosContext.x, this.extraContext.mousePosContext.y, this.extraContext.mousePosContext.z);
        if (this.orthogonalPoint) {
            this.point_move.copy(this.orthogonalPoint);
        }
        if (this.polarPoint) {
            this.point_move.copy(this.polarPoint);
        }
        if (this.capturePoint) {
            this.point_move.copy(this.capturePoint);
        }
        this._helpLine.set(this.point_down, this.point_move);
        this._helpLine.updateViewGeometry();
        let axisX = this.context.ucsContext.getAxisList().axisX;
        let axisZ = this.context.ucsContext.getAxisList().axisZ;
        let direction = this._helpLine._geo.end.clone().sub(this._helpLine._geo.start).normalize();
        let posAngle = direction.angleTo(axisX);
        // let valueZ = axisX.clone().cross(this._helpLine.direction).z;
        let valueZ = this._moveDir.copy(direction).cross(axisX).angleTo(axisZ) * 180 / Math.PI;
        if (valueZ > 90) {
            this.angle = posAngle;
        } else {
            this.angle = 2 * Math.PI - posAngle;
        }
        this.angle_last = this.angle;
        let point = new Point(this.point_down.x, this.point_down.y, this.point_down.z);

        this.objMeshGroups.forEach((objMeshGroup, index) => {
            const objMeshGroupFirstPos = this.objMeshGroupFirstPoss[index];
            const objMeshGroupFirstQuaternion = this.objMeshGroupFirstQuaternions[index];

            //恢复默认位置
            objMeshGroup.position.copy(objMeshGroupFirstPos);
            quaternion.setFromAxisAngle(axisZ, this.angle);
            // 将对象平移到基点坐标系
            objMeshGroup.position.sub(point);
            // 应用旋转
            objMeshGroup.position.applyQuaternion(quaternion);
            // 平移回全局坐标系
            objMeshGroup.position.add(point);
            //恢复默认角度
            objMeshGroup.quaternion.copy(objMeshGroupFirstQuaternion);
            // 如果需要旋转整个对象（包括其方向），应用四元数到对象本身
            objMeshGroup.quaternion.premultiply(quaternion);
        });



        this.editor.listeners.signals.needRender.dispatch();
    }



    // 动态输入窗口识别回车或右键时触发信号
    onDymodeChanged = (value: dymode) => {
        if (this.step === 2) {
            this.point_down.set(value.x, value.y, value.z);
            this.firstStep();
        } else if (this.step === 3) {
            this.angle = value.angle * Math.PI / 180;
            this.secondStep();
        }
    }

    onKeyMouseConfirm = () => {
        if (this.step === 1) {
            this.prevStep();
        } else if (this.step === 2) {
            this.point_down.set(this.extraContext.mousePosContext.x, this.extraContext.mousePosContext.y, this.extraContext.mousePosContext.z);
            if (this.capturePoint) {
                this.point_down.copy(this.capturePoint);
            }
            this.firstStep();
        } else if (this.step === 3) {
            this.secondStep();
        }
    }

}

const quaternion = new Quaternion();